import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { debounce } from "lodash";
import { toast } from "react-toastify";

// Services & Types
import { MemberTrainingService } from "services/memberTraining.service";
import { IMemberTrainingResponseDTO } from "models/church.types";

export default function MemberTrainingList() {
  const [trainings, setTrainings] = useState<IMemberTrainingResponseDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Récupération des filtres contextuels passés dans l'URL
  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);
  const memberIdFromUrl = queryParams.get("memberId");
  const trainingIdFromUrl = queryParams.get("trainingId");

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  /**
   * Charge la liste des suivis de formations en fonction des filtres actifs
   */
  const loadData = async (query: string, page: number) => {
    setLoading(true);
    try {
      const filters = {
        query: query.trim() || undefined,
        memberId: memberIdFromUrl ? Number(memberIdFromUrl) : undefined,
        trainingId: trainingIdFromUrl ? Number(trainingIdFromUrl) : undefined,
      };

      const result = await MemberTrainingService.search(
        page,
        itemsPerPage,
        filters,
      );
      setTrainings(result.data);
      setTotalItems(result.totalCount);
    } catch (error) {
      console.error("Erreur chargement suivi des formations:", error);
      toast.error("Impossible de charger le registre des formations");
    } finally {
      setLoading(false);
    }
  };

  // Déclenchement du rechargement sur changement de page ou de filtres URL
  useEffect(() => {
    loadData(searchTerm, currentPage);
  }, [currentPage, memberIdFromUrl, trainingIdFromUrl]);

  // Recherche optimisée (Debounce)
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setCurrentPage(0);
      loadData(value, 0);
    }, 500),
    [memberIdFromUrl, trainingIdFromUrl],
  );

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleResetFilter = () => {
    setSearchTerm("");
    setCurrentPage(0);
    history.push("/admin/member-trainings"); // Nettoie l'URL de tous les Query Parameters
  };

  const handleDelete = async (id: number) => {
    if (
      window.confirm(
        "Supprimer ce suivi de formation ? L'historique pour ce membre sera perdu.",
      )
    ) {
      setLoading(true);
      try {
        await MemberTrainingService.delete(id);
        toast.success("Suivi de formation supprimé avec succès");
        loadData(searchTerm, currentPage);
      } catch (error) {
        console.error("Erreur suppression suivi formation:", error);
        toast.error("Erreur lors de la suppression de l'enregistrement.");
      } finally {
        setLoading(false);
      }
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const hasActiveFilters = !!(memberIdFromUrl || trainingIdFromUrl);

  return (
    <div className="flex flex-col w-full px-6 min-h-screen">
      {/* TITRE PRINCIPAL */}
      <div className="mb-6 pt-8">
        <h2 className="text-white text-3xl font-bold uppercase tracking-tight">
          Suivi des Formations
        </h2>
        <p className="text-slate-400 text-sm italic">
          {memberIdFromUrl && "Filtrage par membre actif"}
          {trainingIdFromUrl && "Filtrage par session de formation active"}
          {!hasActiveFilters &&
            "Gestion du parcours académique, des notes et des certifications des membres"}
        </p>
      </div>

      {/* BARRE D'OUTILS */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="relative w-full md:w-96 group">
            <span className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-[#D4AF37] transition-colors">
              {loading ? (
                <i className="fas fa-circle-notch animate-spin text-[#D4AF37]"></i>
              ) : (
                <i className="fas fa-search"></i>
              )}
            </span>
            <input
              type="text"
              value={searchTerm}
              onChange={onSearchChange}
              placeholder="Rechercher un membre ou une formation..."
              className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg pl-11 pr-4 py-2 font-sans h-[38px] outline-none focus:border-blue-500 transition-colors"
            />
          </div>

          {/* Bouton de réinitialisation si filtre actif */}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleResetFilter}
              className="bg-slate-700 text-white p-3.5 rounded-xl hover:bg-red-500/20 hover:text-red-500 transition-all border border-slate-600"
              title="Réinitialiser tous les filtres"
            >
              <i className="fas fa-times-circle"></i>
            </button>
          )}
        </div>

        <Link
          to="/admin/member-trainings/register"
          className="bg-[#D4AF37] hover:bg-[#b8952e] text-slate-900 font-bold px-8 h-[38px] flex items-center justify-center rounded-lg whitespace-nowrap transition-colors w-full lg:w-auto text-center text-sm"
        >
          <i className="fas fa-user-graduate mr-2 text-base"></i> Inscrire à une
          formation
        </Link>
      </div>

      {/* TABLE CARD */}
      <div className="relative flex flex-col min-w-0 break-words w-full mb-10 shadow-2xl rounded-2xl bg-slate-800 border border-slate-700 overflow-hidden">
        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse text-left">
            <thead>
              <tr className="bg-[#1E293B] text-slate-300 uppercase text-[10px] font-black tracking-widest border-b border-slate-700">
                <th className="px-6 py-5">Membre</th>
                <th className="px-6 py-5">Formation</th>
                <th className="px-6 py-5 text-center">Note / Évaluation</th>
                <th className="px-6 py-5 text-center">Statut Validation</th>
                <th className="px-6 py-5 text-center">Date de complétion</th>
                <th className="px-6 py-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50 text-slate-300">
              {trainings.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-16 text-slate-500 italic text-sm"
                  >
                    <i className="fas fa-graduation-cap block text-3xl mb-3 opacity-20"></i>
                    Aucun enregistrement de formation trouvé.
                  </td>
                </tr>
              ) : (
                trainings.map((item) => (
                  <tr
                    key={item.id}
                    className="hover:bg-slate-700/40 transition-colors group"
                  >
                    {/* Membre */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center text-[#D4AF37] font-bold mr-4 border border-slate-700">
                          {item.memberLastName?.charAt(0) || "M"}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm font-bold text-white uppercase tracking-tight">
                            {item.memberLastName} {item.memberFirstName}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Formation */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col max-w-xs">
                        <span className="text-xs font-bold text-slate-200 uppercase tracking-tight line-clamp-1">
                          {item.trainingTitle || "Formation non spécifiée"}
                        </span>
                      </div>
                    </td>

                    {/* Note / Évaluation */}
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      <span className="text-sm font-semibold text-slate-300">
                        {item.grade ? (
                          <span className="bg-slate-900 px-3 py-1.5 rounded-lg border border-slate-700 text-[#D4AF37]">
                            {item.grade}
                          </span>
                        ) : (
                          <span className="text-slate-500 italic text-xs">
                            Pas de note
                          </span>
                        )}
                      </span>
                    </td>

                    {/* Statuts (Complété & Certificat) */}
                    <td className="px-6 py-4 text-center whitespace-nowrap">
                      <div className="flex flex-col items-center gap-1.5">
                        {item.completed ? (
                          <span className="inline-flex items-center bg-green-500/10 text-green-400 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full border border-green-500/20 w-28 justify-center">
                            <span className="w-1.5 h-1.5 rounded-full bg-green-400 mr-1.5 animate-pulse"></span>
                            Complété
                          </span>
                        ) : (
                          <span className="inline-flex items-center bg-amber-500/10 text-amber-400 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full border border-amber-500/20 w-28 justify-center">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-1.5"></span>
                            En cours
                          </span>
                        )}

                        {item.certificateIssued && (
                          <span className="inline-flex items-center bg-blue-500/10 text-blue-400 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded border border-blue-500/20">
                            <i className="fas fa-certificate mr-1 text-[10px]"></i>{" "}
                            Certificat Délivré
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Date de complétion */}
                    <td className="px-6 py-4 text-[10px] font-medium text-center uppercase whitespace-nowrap">
                      {item.completionDate ? (
                        <span className="bg-slate-900/40 px-2.5 py-1.5 rounded text-slate-400 border border-slate-700/40">
                          {new Date(item.completionDate).toLocaleDateString(
                            "fr-FR",
                            {
                              dateStyle: "medium",
                            },
                          )}
                        </span>
                      ) : (
                        <span className="text-slate-600 italic font-normal">
                          -
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-5">
                        <Link
                          to={`/admin/member-trainings/edit/${item.id}`}
                          className="text-slate-400 hover:text-[#D4AF37] transition-colors"
                          title="Modifier le suivi académique"
                        >
                          <i className="fas fa-edit text-lg"></i>
                        </Link>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          title="Supprimer l'inscription"
                        >
                          <i className="fas fa-trash-alt text-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="px-6 py-5 bg-[#0F172A]/50 border-t border-slate-700 flex items-center justify-between">
          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider">
            Total : <span className="text-[#D4AF37]">{totalItems}</span>{" "}
            enregistrements
          </p>
          <div className="flex gap-2">
            <button
              disabled={currentPage === 0}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Précédent
            </button>
            <button
              disabled={currentPage + 1 >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)} // Correction appliquée ici (p + 1)
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
